<template>
  <div class="member-application">
    <h1 class="page-title">Pendaftaran Member Baru</h1>

    <div class="filter-bar">
      <a-input-search
        v-model:value="searchQuery"
        placeholder="Cari nama pendaftar atau nomor pendaftaran"
        allow-clear
        class="filter-search"
        @search="handleSearch"
        @change="e => !e.target.value && handleSearch()"
      />
      <a-select
        v-model:value="statusFilter"
        placeholder="Semua Status"
        allow-clear
        class="filter-select"
        @change="handleStatusFilter"
      >
        <a-select-option value="">Semua Status</a-select-option>
        <a-select-option value="pending">Verifikasi Admin</a-select-option>
        <a-select-option value="revision">Menunggu Revisi</a-select-option>
        <a-select-option value="approved">Disetujui</a-select-option>
        <a-select-option value="rejected">Ditolak</a-select-option>
      </a-select>
      <a-select
        v-model:value="cabangFilter"
        placeholder="Semua Cabang"
        allow-clear
        show-search
        class="filter-select"
        @change="handleCabangFilter"
        @clear="clearCabangFilter"
      >
        <a-select-option value="">Semua Cabang</a-select-option>
        <a-select-option v-for="branch in branches" :key="branch" :value="branch">{{
          branch
        }}</a-select-option>
      </a-select>
    </div>

    <a-table
      :columns="columns"
      :data-source="applications"
      :loading="loading"
      :pagination="pagination"
      :custom-row="record => ({ onClick: () => goToDetail(record.id) })"
      row-key="id"
      class="clickable-table"
      @change="handleTableChange"
    >
      <template #nomorPendaftaran="{ record }">
        {{ record.nomorPendaftaran || '-' }}
      </template>

      <template #status="{ record }">
        <a-tag :color="getStatusConfig(record.status).color">
          {{ getStatusConfig(record.status).label }}
        </a-tag>
      </template>

      <template #tanggal="{ record, column }">
        {{ formatDate(record[column.dataIndex]) }}
      </template>
    </a-table>
  </div>
</template>

<script>
import { computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useMemberApplication } from './composables/useMemberApplication'

function formatDate(iso) {
  if (!iso) return '-'
  try {
    const dt = new Date(iso)
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'Mei',
      'Jun',
      'Jul',
      'Agu',
      'Sep',
      'Okt',
      'Nov',
      'Des',
    ]
    const h = String(dt.getHours()).padStart(2, '0')
    const m = String(dt.getMinutes()).padStart(2, '0')
    return `${dt.getDate()} ${months[dt.getMonth()]} ${dt.getFullYear()} ${h}:${m}`
  } catch {
    return '-'
  }
}

const branches = [
  'Aceh',
  'Bali',
  'Balikpapan',
  'Bandung Raya',
  'Bangka Belitung',
  'Banten',
  'Bekasi - Karawang',
  'Bengkulu',
  'Bogor',
  'Cirebon',
  'Depok',
  'Gorontalo',
  'Jakarta',
  'Jambi',
  'Kalimantan Barat',
  'Kalimantan Selatan',
  'Kalimantan Tengah',
  'Kalimantan Utara',
  'Kendari',
  'Kepulauan Riau',
  'Lampung',
  'Makassar',
  'Malang',
  'Maluku',
  'Nusa Tenggara Barat',
  'Nusa Tenggara Timur',
  'Palangkaraya',
  'Papua',
  'Pekalongan',
  'Purwokerto',
  'Riau',
  'Samarinda',
  'Semarang',
  'Solo',
  'Sulawesi Tengah',
  'Sulawesi Utara',
  'Sumatera Barat',
  'Sumatera Selatan',
  'Sumatera Utara',
  'Surabaya',
  'Tasikmalaya',
  'Tegal',
  'Yogyakarta',
]

const columns = [
  {
    title: 'No. Pendaftaran',
    key: 'nomorPendaftaran',
    width: 150,
    slots: { customRender: 'nomorPendaftaran' },
  },
  { title: 'Nama', dataIndex: 'namaLengkap', key: 'namaLengkap' },
  {
    title: 'Tanggal Pengajuan',
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: 180,
    sorter: true,
    slots: { customRender: 'tanggal' },
  },
  { title: 'PERKI Cabang', dataIndex: 'perkiCabang', key: 'perkiCabang', width: 160 },
  {
    title: 'Terakhir Diubah',
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    width: 180,
    sorter: true,
    slots: { customRender: 'tanggal' },
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    width: 160,
    slots: { customRender: 'status' },
  },
]

export default {
  name: 'MemberApplication',
  setup() {
    const router = useRouter()
    const route = useRoute()

    // Antd select allows multiple or single. We'll pass the exact query.status as array or string.
    const initialStatus = route.query.status
      ? Array.isArray(route.query.status)
        ? route.query.status.join(',')
        : route.query.status
      : ''

    const composable = useMemberApplication(initialStatus)

    onMounted(composable.loadApplications)

    const goToDetail = id => router.push(`/mobile-cms/member-application/${id}`)

    return { ...composable, columns, goToDetail, formatDate, branches }
  },
}
</script>

<style scoped>
.member-application {
  padding: 0;
}

.page-title {
  font-size: 30px;
  font-weight: 700;
  color: #282828;
  margin-bottom: 16px;
}

.filter-bar {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.filter-search {
  flex: 1;
  min-width: 240px;
}

.filter-select {
  width: 180px;
}

.clickable-table :deep(tr.ant-table-row) {
  cursor: pointer;
}

.clickable-table :deep(tr.ant-table-row:hover td) {
  background: #fff1f0 !important;
}
</style>
